import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setNavigate } from '@/utils/navigation';
import { ToastContainer } from '@liveauctioneers/hammer-ui-core/toast';
import Auth from '@/components/Auth/Auth';
import Layout from '@/components/Layout/Layout';
import Modals from '@/components/Modals/Modals';
import PrivateRoute from '@/components/PrivateRoute/PrivateRoute';
import ReviewsPage from '../Reviews/ReviewsPage';
import styled from 'styled-components';
import WhiteLabelPage from '../WhiteLabel/WhiteLabelPage';
import '@liveauctioneers/hammer-ui-theme/css/LiveAuctioneers.css';

const HouseSettingsPage = lazy(() => import('../HouseSettings/HouseSettingsPageContainer'));
const BidderPage = lazy(() => import('../Bidder/BidderPage'));
const BidPercentPage = lazy(() => import('../BidPercent/BidPercentPageContainer'));
const BINPage = lazy(() => import('../BuyItNow/BINPage'));
const CatalogPage = lazy(() => import('../Catalog/CatalogPage'));
const CategorizationPage = lazy(() => import('../Categorization/CategorizationPageContainer'));
const FinanceDashboardPage = lazy(() => import('../FinanceDashboard/FinanceDashboardPage'));
const HealthPage = lazy(() => import('../Health/HealthPageContainer'));
const J52Page = lazy(() => import('../J52/J52Page'));
const LoginPage = lazy(() => import('../Login/LoginPageContainer'));
const MarketingPage = lazy(() => import('../Marketing/MarketingPageContainer'));
const NotFoundPage = lazy(() => import('../NotFound/NotFoundPageContainer'));
const StatusPage = lazy(() => import('../Status/StatusPage'));
const LiveShipPage = lazy(() => import('../LiveShip/LiveShipPage'));
const QuickCreatePage = lazy(() => import('../QuickCreate/QuickCreate'));
const ATGPayPage = lazy(() => import('../ATGPay/ATGPay'));
const SecurityPage = lazy(() => import('../Security/SecurityPage'));

const App = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Set navigate function for use in Redux actions
        setNavigate(navigate);
    }, [navigate]);

    return (
        <div>
            <Auth />
            <main>
                <Layout>
                    <Suspense fallback={<StyledPageContainer>Loading...</StyledPageContainer>}>
                        <Routes>
                            <Route
                                element={
                                    pathname.endsWith('/') && pathname !== '/' ? (
                                        <Navigate
                                            replace
                                            to={pathname.slice(0, -1)}
                                        />
                                    ) : null
                                }
                                path="/:url*(/+)"
                            />
                            <Route
                                element={<LoginPage />}
                                path="/"
                            />
                            <Route
                                element={<LoginPage />}
                                path="/login"
                            />
                            <Route
                                element={<PrivateRoute component={HealthPage} />}
                                path="/health"
                            />
                            <Route
                                element={<PrivateRoute component={J52Page} />}
                                path="/j52"
                            />
                            <Route
                                element={<PrivateRoute component={BidderPage} />}
                                path="/bidder/*"
                            />
                            <Route
                                element={<PrivateRoute component={BidPercentPage} />}
                                path="/bidpercent/:catalogId"
                            />
                            <Route
                                element={<PrivateRoute component={BINPage} />}
                                path="/bin"
                            />
                            <Route
                                element={<PrivateRoute component={CatalogPage} />}
                                path="/catalog/*"
                            />
                            <Route
                                element={<PrivateRoute component={CategorizationPage} />}
                                path="/categorization/*"
                            />
                            <Route
                                element={<PrivateRoute component={MarketingPage} />}
                                path="/marketing/*"
                            />
                            <Route
                                element={<PrivateRoute component={StatusPage} />}
                                path="/status/*"
                            />
                            <Route
                                element={<PrivateRoute component={FinanceDashboardPage} />}
                                path="/finance-dashboard/*"
                            />
                            <Route
                                element={<PrivateRoute component={HouseSettingsPage} />}
                                path="/house-settings/*"
                            />
                            <Route
                                element={<PrivateRoute component={WhiteLabelPage} />}
                                path="/white-label/:houseId?"
                            />
                            <Route
                                element={<PrivateRoute component={ReviewsPage} />}
                                path="/reviews"
                            />
                            <Route
                                element={<PrivateRoute component={LiveShipPage} />}
                                path="/live-ship/*"
                            />
                            <Route
                                element={<PrivateRoute component={QuickCreatePage} />}
                                path="/quick-create/*"
                            />
                            <Route
                                element={<PrivateRoute component={ATGPayPage} />}
                                path="/atgpay/*"
                            />
                            <Route
                                element={<PrivateRoute component={SecurityPage} />}
                                path="/security/*"
                            />
                            <Route
                                element={<NotFoundPage />}
                                path="*"
                            />
                        </Routes>
                    </Suspense>
                </Layout>
            </main>
            <ToastContainer />
            <Modals />
        </div>
    );
};

export default App;

const StyledPageContainer = styled.div`
    max-width: 1440px;
    width: 100%;
    margin: 0 auto;
    padding: 60px 20px 0;
`;
